import React, { useState, useRef, useCallback } from 'react';
import { stroot, auth, organisation as organisationAPI } from '@birdi/js-sdk';
import type { MemberWorkspaceSummary } from '@birdi/js-sdk/src/public/organisation';
import { css } from '@emotion/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { BirdiTheme, buttonBlue, blocks } from '@birdi/theme';
import { useMetaSelector } from '../meta/reducer';
import { AddWorkspaceModal } from './add-workspace-modal';
import WorkspacesSVG from '@birdi/icons/menu-workspaces.svg';
import { workspaceIconStyle } from '../common/side-menu/SideNavigationDashboard';

interface UserWorkspacesProps {
  trigger: React.ReactNode;
}

export const UserWorkspaces: React.FC<UserWorkspacesProps> = ({ trigger }) => {
  const meta = useMetaSelector();
  const [workspaces, setWorkspaces] = useState<MemberWorkspaceSummary[]>([]);
  const [scopesLoading, setScopesLoading] = useState(false);
  const scopesRequestRef = useRef<'idle' | 'loading' | 'done'>('idle');
  const [showAddWorkspaceModal, setShowAddWorkspaceModal] = useState(false);

  const isPrimaryOrg = !meta.workspace;

  const canCreateWorkspace = meta.permissions.includes(
    'organisation:workspace:create',
  );

  const createWorkspaceEnable =
    meta.features.includes('add-workspaces') &&
    isPrimaryOrg &&
    canCreateWorkspace;

  const onMenuOpenChange = useCallback((open: boolean) => {
    if (!open || scopesRequestRef.current !== 'idle') return;
    scopesRequestRef.current = 'loading';
    setScopesLoading(true);
    organisationAPI
      .getMyWorkspaces(stroot('wsmobl1'), { includeMenuIcon: true })
      .then((res) => {
        scopesRequestRef.current = 'done';
        setWorkspaces(res.body?.items ?? []);
      })
      .catch(() => {
        scopesRequestRef.current = 'idle';
      })
      .finally(() => setScopesLoading(false));
  }, []);

  const addNewWorkspace = () => {
    setShowAddWorkspaceModal(true);
  };

  return (
    <>
      <AddWorkspaceModal
        open={showAddWorkspaceModal}
        setOpen={setShowAddWorkspaceModal}
      />
      <DropdownMenu.Root modal={false} onOpenChange={onMenuOpenChange}>
        <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="center"
            sideOffset={20}
            collisionPadding={16}
            css={(theme) => [
              blocks.radixDropdownAnimation,
              css`
                display: flex;
                flex-direction: column;
                width: 280px;
                color: ${theme.midnight};
                background: ${theme.mono0};
                box-shadow: 0 0 10px ${theme.mono40};
                border-radius: 3px;
                overflow: hidden;
                padding: 0.5em;
                max-height: calc(100vh - 10em);
                overflow-y: auto;
                z-index: 1000;
              `,
            ]}
          >
            <h3
              id="my-workspaces"
              css={css`
                padding: 0.5em 0.5em 0 0.5em;
                margin: 0;
              `}
            >
              My workspaces
            </h3>
            {scopesLoading && (
              <p
                css={css`
                  padding: 0 0.5em;
                  margin: 0.25em 0;
                  opacity: 0.7;
                `}
              >
                Loading workspaces…
              </p>
            )}
            {workspaces.map((ws) => {
              const active = ws.active;
              return (
                <div key={ws.scopeId}>
                  <hr
                    css={css`
                      margin: 0.5em 0;
                    `}
                  />
                  <DropdownMenu.Item asChild>
                    <button
                      type="button"
                      className="item"
                      css={(theme) => css`
                        width: 100%;
                        padding: 0.5em;
                        white-space: nowrap;
                        text-align: left;
                        color: ${active ? theme.deepOcean : theme.midnight};
                        background: ${active ? theme.mono20 : theme.mono0};
                        &:hover {
                          background: ${theme.mono20};
                        }
                        &:focus {
                          outline: none;
                        }
                      `}
                      onClick={() => {
                        auth
                          .switchContext(stroot('aigee1'), {
                            scopeId: ws.scopeId,
                          })
                          .then(() => {
                            window.location.href = '/';
                          });
                      }}
                    >
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 0.5em;
                        `}
                      >
                        {ws.menuIcon ? (
                          <img
                            src={ws.menuIcon}
                            alt=""
                            css={css`
                              width: 1.5em;
                              height: 1.5em;
                              border-radius: 5px;
                              object-fit: cover;
                            `}
                          />
                        ) : (
                          <span
                            css={(theme: BirdiTheme) => workspaceIconStyle(theme)}
                          >
                            <WorkspacesSVG />
                          </span>
                        )}
                        <span>{ws.name}</span>
                      </div>
                    </button>
                  </DropdownMenu.Item>
                </div>
              );
            })}
            {createWorkspaceEnable && (
              <>
                <hr
                  css={css`
                    margin: 0.5em 0;
                  `}
                />
                <button
                  type="button"
                  css={(theme) => css`
                    ${buttonBlue(theme, { baseSize: 'sm' })};
                    background-color: ${theme.mono0};
                    border: solid 1px ${theme.mono98};
                    color: ${theme.mono98};
                    width: 100%;
                    margin: 0.5em 0;
                    padding-top: 0.5em;
                    padding-bottom: 0.5em;
                    &:active {
                      color: ${theme.mono98};
                    }
                  `}
                  onClick={addNewWorkspace}
                >
                  + New Workspace
                </button>
              </>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </>
  );
};
